import { useState, useEffect, useCallback } from "react";
import { X, Loader2, Wand2, Check, Users } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { opts } from "./constants";

function scoreBadge(score) {
  if (score >= 0.7) return "bg-emerald-100 text-emerald-700";
  if (score >= 0.5) return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-500";
}

export function MapeoMORubroModal({ open, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rubros, setRubros] = useState([]);
  const [rows, setRows] = useState([]); // {boca_id, tipo_boca, grupo, mo_rubro_id_actual, sugerido, selected}

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bocas-precios/mapeo-mo/sugerencias`, opts);
      setRubros(data.rubros || []);
      setRows((data.bocas || []).map(b => ({
        ...b,
        selected: b.mo_rubro_id_actual || "",
      })));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar sugerencias de mapeo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  if (!open) return null;

  const nSinMapear = rows.filter(r => !r.mo_rubro_id_actual).length;
  const nConSugerencia = rows.filter(r => !r.mo_rubro_id_actual && r.sugerido).length;

  const setRowSelected = (bocaId, value) => {
    setRows(prev => prev.map(r => r.boca_id === bocaId ? { ...r, selected: value } : r));
  };

  const acceptHighConfidence = () => {
    const count = rows.filter(r => r.sugerido && r.sugerido.score >= 0.7 && !r.selected).length;
    setRows(prev => prev.map(r => (r.sugerido && r.sugerido.score >= 0.7 && !r.selected)
      ? { ...r, selected: r.sugerido.rubro_id }
      : r));
    toast.success(count > 0 ? `${count} sugerencia(s) de alta confianza (≥70%) aplicada(s) — revisá y guardá` : "No hay sugerencias de alta confianza (≥70%) pendientes");
  };

  const handleSave = async () => {
    const mappings = rows
      .filter(r => (r.selected || "") !== (r.mo_rubro_id_actual || ""))
      .map(r => ({ boca_id: r.boca_id, mo_rubro_id: r.selected || null }));
    if (mappings.length === 0) { toast.info("No hay cambios para guardar"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/bocas-precios/mapeo-mo/confirmar`, { mappings }, opts);
      toast.success(`${data.updated} boca${data.updated !== 1 ? "s" : ""} actualizada${data.updated !== 1 ? "s" : ""}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el mapeo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="mapeo-mo-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Wand2 size={16} className="text-amber-500" /> Mapeo asistido Boca ↔ Rubro de M.O.
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading ? "Cargando sugerencias..." : `${nSinMapear} boca${nSinMapear !== 1 ? "s" : ""} sin mapear · ${nConSugerencia} con sugerencia automática`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="mapeo-mo-close-btn"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={28} /></div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">La sugerencia se calcula comparando el nombre de la boca con la descripción de cada rubro. Revisá y ajustá antes de guardar.</p>
                <button onClick={acceptHighConfidence}
                  className="flex-shrink-0 flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
                  data-testid="mapeo-mo-accept-high-btn">
                  <Check size={13} /> Aceptar sugerencias ≥70%
                </button>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#EBF3FB]">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Boca</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Sugerencia</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Rubro asignado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.boca_id} className="border-t border-gray-100" data-testid={`mapeo-mo-row-${r.boca_id}`}>
                        <td className="px-3 py-2 font-semibold text-gray-800">{r.tipo_boca}</td>
                        <td className="px-3 py-2">
                          {r.sugerido ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${scoreBadge(r.sugerido.score)}`}>
                              {r.sugerido.descripcion} · {Math.round(r.sugerido.score * 100)}%
                            </span>
                          ) : (
                            <span className="text-gray-300 text-[10px]">sin sugerencia</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <select value={r.selected || ""} onChange={e => setRowSelected(r.boca_id, e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1 text-[11px] bg-white"
                            data-testid={`mapeo-mo-select-${r.boca_id}`}>
                            <option value="">— Sin mapear —</option>
                            {rubros.map(rb => <option key={rb.id} value={rb.id}>{rb.descripcion}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving || loading}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="mapeo-mo-save-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />}
            {saving ? "Guardando..." : "Confirmar y guardar mapeo"}
          </button>
        </div>
      </div>
    </div>
  );
}
